import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { MapPin, Loader } from 'lucide-react';
import { apiGet } from '../lib/api';
import { summaryLabel } from '../lib/addresses';
import type { AddressSummary } from '../types';
import './AddressCombobox.css';

interface AddressComboboxProps {
    id: string;
    /** The address the resident has settled on, or null while they are typing. */
    value: AddressSummary | null;
    onSelect: (address: AddressSummary | null) => void;
    disabled?: boolean;
    placeholder?: string;
    /** id(s) of the hint text under the field. */
    describedBy?: string;
    /** Wait before asking the server. Tests pass 0. */
    debounceMs?: number;
}

const MIN_QUERY = 2;

/**
 * Accessible address autocomplete over `GET /api/addresses`.
 *
 * ARIA 1.2 combobox: the input owns the listbox, keeps `aria-activedescendant`
 * on the highlighted option and never moves DOM focus into the list, so a
 * screen reader reads options while typing carries on normally.
 *
 * The endpoint answers anonymous callers (sign-up needs it) and never sends
 * names or counts — only whether an address already has someone registered.
 */
export function AddressCombobox({
    id, value, onSelect, disabled = false, placeholder = 'Start typing your street number…',
    describedBy, debounceMs = 250,
}: AddressComboboxProps) {
    const listId = `${id}-listbox`;
    const statusId = useId();
    const [query, setQuery] = useState(value ? summaryLabel(value) : '');
    const [options, setOptions] = useState<AddressSummary[]>([]);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const blurTimer = useRef<number | undefined>(undefined);

    // The selected label is what the input shows; typing again clears the
    // selection so the form can never submit an address nobody picked.
    useEffect(() => {
        if (value) setQuery(summaryLabel(value));
    }, [value]);

    useEffect(() => () => window.clearTimeout(blurTimer.current), []);

    const search = useCallback(async (q: string) => {
        setLoading(true);
        try {
            const { addresses } = await apiGet<{ addresses: AddressSummary[] }>('/api/addresses', { q });
            setOptions(addresses ?? []);
            setActive(-1);
            setOpen(true);
        } catch {
            // A failed lookup must not block sign-up: the free-text escape
            // hatch under the field is always there.
            setOptions([]);
        } finally {
            setLoading(false);
            setSearched(true);
        }
    }, []);

    useEffect(() => {
        const q = query.trim();
        if (value && q === summaryLabel(value)) return;
        if (q.length < MIN_QUERY) {
            setOptions([]);
            setOpen(false);
            setSearched(false);
            return;
        }
        const t = window.setTimeout(() => { void search(q); }, debounceMs);
        return () => window.clearTimeout(t);
    }, [query, value, search, debounceMs]);

    const choose = (option: AddressSummary) => {
        onSelect(option);
        setQuery(summaryLabel(option));
        setOpen(false);
        setActive(-1);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (options.length === 0) return;
            e.preventDefault();
            setOpen(true);
            setActive(prev => {
                if (e.key === 'ArrowDown') return prev + 1 >= options.length ? 0 : prev + 1;
                return prev <= 0 ? options.length - 1 : prev - 1;
            });
            return;
        }
        if (e.key === 'Home' && open && options.length > 0) { e.preventDefault(); setActive(0); return; }
        if (e.key === 'End' && open && options.length > 0) { e.preventDefault(); setActive(options.length - 1); return; }
        if (e.key === 'Enter') {
            if (open && active >= 0 && options[active]) {
                e.preventDefault();
                choose(options[active]);
            }
            return;
        }
        if (e.key === 'Escape') {
            if (open) { e.preventDefault(); setOpen(false); setActive(-1); }
        }
    };

    const showEmpty = open && searched && !loading && options.length === 0;

    return (
        <div className="address-combobox">
            <div className="input-wrapper">
                <MapPin className="input-icon" size={20} aria-hidden="true" />
                <input
                    id={id}
                    type="text"
                    className="input input-with-icon"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listId}
                    aria-autocomplete="list"
                    aria-haspopup="listbox"
                    aria-activedescendant={open && active >= 0 ? `${listId}-opt-${active}` : undefined}
                    aria-describedby={[describedBy, statusId].filter(Boolean).join(' ') || undefined}
                    autoComplete="off"
                    placeholder={placeholder}
                    value={query}
                    disabled={disabled}
                    maxLength={200}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        if (value) onSelect(null);
                    }}
                    onKeyDown={onKeyDown}
                    onFocus={() => { if (options.length > 0) setOpen(true); }}
                    onBlur={() => {
                        // Let a click on an option land before the list closes.
                        blurTimer.current = window.setTimeout(() => setOpen(false), 150);
                    }}
                />
                {loading && <Loader className="address-combobox-spinner spinner-icon" size={18} aria-hidden="true" />}
            </div>

            <ul
                className={`address-combobox-list ${open && (options.length > 0 || showEmpty) ? 'is-open' : ''}`}
                id={listId}
                role="listbox"
                aria-label="Matching Forest Town addresses"
            >
                {options.map((o, i) => (
                    <li
                        key={o.id}
                        id={`${listId}-opt-${i}`}
                        role="option"
                        aria-selected={i === active}
                        className={`address-combobox-option ${i === active ? 'is-active' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => choose(o)}
                    >
                        <span className="address-combobox-label">{summaryLabel(o)}</span>
                        {o.registered && <span className="address-combobox-tag">Already registered</span>}
                    </li>
                ))}
                {showEmpty && (
                    <li className="address-combobox-empty" role="presentation">
                        No match yet. Keep typing, or use "My address isn't listed" below.
                    </li>
                )}
            </ul>

            <span id={statusId} className="sr-only" role="status">
                {loading
                    ? 'Searching addresses'
                    : value
                        ? `${summaryLabel(value)} selected`
                        : options.length > 0
                            ? `${options.length} address${options.length === 1 ? '' : 'es'} found. Use the arrow keys to choose one.`
                            : ''}
            </span>
        </div>
    );
}
